/**
 * GetNetworkTenantsQuery - Fetches network tenants for admin dropdown
 * Follows MediatR pattern (Query + Handler in same file)
 */

import type { IQuery, IQueryHandler, IHttpClient, BaseApiResponse } from '@/application/core';
import type { NetworkTenantsResponse, NetworkTenantItem } from './network-tenants.types';
import { toCamelCase } from '@/utils/caseTransform';

// ============================================================================
// Query (Request)
// ============================================================================

export class GetNetworkTenantsQuery implements IQuery<NetworkTenantsResponse> {
  readonly type = 'GetNetworkTenantsQuery' as const;
}

// ============================================================================
// Handler
// ============================================================================

export class GetNetworkTenantsQueryHandler
  implements IQueryHandler<GetNetworkTenantsQuery, NetworkTenantsResponse>
{
  constructor(private readonly httpClient: IHttpClient) {}

  async handle(_query: GetNetworkTenantsQuery): Promise<NetworkTenantsResponse> {
    try {
      const response = await this.httpClient.get<BaseApiResponse<any>>(
        '/api/v1/network-tenants'
      );

      if (!response.succeeded) {
        throw new Error(response.message || 'Failed to fetch network tenants');
      }

      const data = toCamelCase(response.data);

      // Transform each tenant item
      const networkTenants: NetworkTenantItem[] = (
        data.networkTenants ?? data.NetworkTenants ?? []
      ).map((t: any) => toCamelCase(t));

      return {
        networkTenants,
        hostDomain: data.hostDomain ?? data.HostDomain ?? 'ecgrid.com',
      };
    } catch (error: any) {
      // 403 = user lacks admin role; return empty (defensive)
      if (error.response?.status === 403 || error.status === 403) {
        return { networkTenants: [], hostDomain: 'ecgrid.com' };
      }
      throw error;
    }
  }
}
